import React, { useState, useRef, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import TextField from '@mui/material/TextField';
import esLocale from 'dayjs/locale/es';
import dayjs from 'dayjs';
import { useParams, useNavigate } from 'react-router-dom';
import {
  buscarReservaPorCodigo,
  eliminarHabitacionDeReserva,
  anularReservaPorCodigo,
  actualizarPersonasEnHabitacion,
  actualizarFechasReserva
} from '../data/reservas';
import { enviarCorreoConfirmacion } from '../services/emailService';
import './GestionReservas.css';
import Modal from './Modal';
import { formatearFecha, formatearPrecio } from '../utils/dateUtils';
import { db } from '../firebase/firebaseConfig';
import { collection, query, where, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';

// Calcular la diferencia en días entre dos fechas
const calcularDiferenciaDias = (fechaInicio, fechaFin) => {
  const inicio = new Date(fechaInicio);
  const fin = new Date(fechaFin);
  const diferencia = fin.getTime() - inicio.getTime();
  return Math.ceil(diferencia / (1000 * 3600 * 24));
};

// Función para formatear fechas de YYYY-MM-DD a DD-MM-AAAA solo para visualización
const formatearFechaVisual = (fechaStr) => {
  if (!fechaStr) return '';
  
  // Verificar que la fecha tenga el formato esperado YYYY-MM-DD
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fechaStr)) return fechaStr;
  
  // Convertir de YYYY-MM-DD a DD-MM-AAAA
  const [anio, mes, dia] = fechaStr.split('-');
  return `${dia}-${mes}-${anio}`;
};

function GestionReservas() {
  const [codigoReserva, setCodigoReserva] = useState('');
  const [loading, setLoading] = useState(false);
  const [reservaInfo, setReservaInfo] = useState(null);
  const [errorMensaje, setErrorMensaje] = useState('');
  const [modalAbierto, setModalAbierto] = useState(false);
  const [modalTipo, setModalTipo] = useState('');
  const [modalMensaje, setModalMensaje] = useState('');
  const [modalTitulo, setModalTitulo] = useState('');
  const [habitacionSeleccionada, setHabitacionSeleccionada] = useState(null);
  const [nuevaCantidadPersonas, setNuevaCantidadPersonas] = useState(1);
  const [nuevasFechas, setNuevasFechas] = useState({ entrada: null, salida: null });
  const [totalRecalculado, setTotalRecalculado] = useState(0);
  
  // Nuevos estados para acumular cambios
  const [cambiosPendientes, setCambiosPendientes] = useState({
    habitacionesEliminar: [],
    cambiosFechas: null,
    cambiosPersonas: {}
  });
  const [mostrarResumen, setMostrarResumen] = useState(false);
  const [preciosRecalculados, setPreciosRecalculados] = useState({});
  
  // Nuevo estado para el precio total inicial calculado con datos de Firestore
  const [precioInicialFirestore, setPrecioInicialFirestore] = useState(0);
  
  // Nuevo estado para datos de habitaciones y carga
  const [habitacionesData, setHabitacionesData] = useState([]);
  const [loadingHabitaciones, setLoadingHabitaciones] = useState(true);
  
  const inputCodigoRef = useRef(null);
  const params = useParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Si hay un código de reserva en los parámetros de la URL, buscar la reserva
    if (params.codigoReserva) {
      setCodigoReserva(params.codigoReserva);
      buscarReservaPorCodigoDirecto(params.codigoReserva);
    } else if (inputCodigoRef.current) {
      // Si no hay código en URL, enfocar el input
      inputCodigoRef.current.focus();
    }
  }, [params.codigoReserva]);
  
  // Efecto para cargar datos de habitaciones desde Firestore
  useEffect(() => {
    const fetchHabitaciones = async () => {
      setLoadingHabitaciones(true);
      try {
        const habitacionesCollection = collection(db, 'habitaciones');
        const habitacionesSnapshot = await getDocs(habitacionesCollection);
        const habitacionesList = habitacionesSnapshot.docs.map(doc => ({
          id: parseInt(doc.id, 10), // Asegurar que el ID sea número si se usa como tal
          ...doc.data()
        }));
        // Ordenar por ID si es necesario (opcional)
        habitacionesList.sort((a, b) => a.id - b.id);
        setHabitacionesData(habitacionesList);
        console.log('Habitaciones cargadas desde Firestore:', habitacionesList);
      } catch (error) {
        console.error("Error al cargar habitaciones desde Firestore:", error);
        setErrorMensaje("Error al cargar datos de habitaciones."); // Mostrar error al usuario
      } finally {
        setLoadingHabitaciones(false);
      }
    };

    fetchHabitaciones();
  }, []); // Se ejecuta solo una vez al montar el componente
  
  // Calcular precio por cantidad de personas
  const calcularPrecioPorPersonas = (habitacionId, personas) => {
    // Primero verificar si es una habitación de una reserva existente
    if (reservaInfo && reservaInfo.habitaciones) {
      // Buscar si esta habitación existe en la reserva actual
      const habitacionEnReserva = reservaInfo.habitaciones.find(
        h => h.habitacionId.toString() === habitacionId.toString()
      );
      
      // Si encontramos la habitación, verificar si tiene un precio personalizado guardado
      if (habitacionEnReserva) {
        console.log("Verificando precio personalizado para habitación:", habitacionId);
        // Intentar obtener el precio personalizado en este orden: precioPorNoche, pricePerNight, precioHabitacion, roomPrice
        if (habitacionEnReserva.precioPorNoche) {
          console.log("Usando precio personalizado (precioPorNoche):", habitacionEnReserva.precioPorNoche);
          return habitacionEnReserva.precioPorNoche;
        } else if (habitacionEnReserva.pricePerNight) {
          console.log("Usando precio personalizado (pricePerNight):", habitacionEnReserva.pricePerNight);
          return habitacionEnReserva.pricePerNight;
        } else if (habitacionEnReserva.precioHabitacion) {
          console.log("Usando precio de habitación guardado (precioHabitacion):", habitacionEnReserva.precioHabitacion);
          return habitacionEnReserva.precioHabitacion;
        } else if (habitacionEnReserva.roomPrice) {
          console.log("Usando precio de habitación guardado (roomPrice):", habitacionEnReserva.roomPrice);
          return habitacionEnReserva.roomPrice;
        }
      }
    }
    
    // Si no se encontró un precio personalizado, calcular basado en ocupación
    const habitacion = obtenerInfoHabitacion(habitacionId);
    if (habitacion && habitacion.preciosPorOcupacion) {
      // Asegurarse que 'personas' sea un número válido
      const personasNum = parseInt(personas, 10);
      if (isNaN(personasNum)) return habitacion.precio || 0; // Si no es número válido, devolver precio base

      // Buscar precio por ocupación o devolver precio base si no existe
      return habitacion.preciosPorOcupacion[personasNum] || habitacion.precio || 0;
    }
    return habitacion?.precio || 0; // Devolver precio base si no hay precios por ocupación
  };
  
  // Calcular precio total
  const calcularPrecioTotal = (precioBase, fechaInicio, fechaFin) => {
    const dias = calcularDiferenciaDias(fechaInicio, fechaFin);
    return precioBase * dias;
  };
  
  // Función para obtener información de una habitación
  const obtenerInfoHabitacion = (habitacionId) => {
    if (loadingHabitaciones || habitacionesData.length === 0) {
      return { nombre: 'Cargando...', capacity: 0, tipo: 'Cargando...', precio: 0, preciosPorOcupacion: {} };
    }
    const habitacionEncontrada = habitacionesData.find(h => h.id.toString() === habitacionId.toString());
    // Solo loguear si no se encuentra la habitación
    if (!habitacionEncontrada) {
      console.warn(`No se encontró información para la habitación ID ${habitacionId}`);
    }
    return habitacionEncontrada || { nombre: 'Desconocida', capacity: 0, tipo: 'Desconocido', precio: 0, preciosPorOcupacion: {} };
  };

  // Calcular precio desde datos actuales de habitaciones (Firestore)
  // Nota: se usa para recalcular cuando cambian personas/fechas, evitando reutilizar el precio guardado en la reserva.
  const calcularPrecioPorPersonasDesdeHabitaciones = (habitacionId, personas) => {
    const infoHabitacion = obtenerInfoHabitacion(habitacionId);
    const personasNum = parseInt(personas, 10) || 1;
    const preciosPorOcupacion = infoHabitacion?.preciosPorOcupacion || {};
    const precioOcupacion = preciosPorOcupacion[personasNum];
    if (precioOcupacion !== undefined && precioOcupacion !== null) {
      return parseInt(precioOcupacion, 10) || 0;
    }
    return parseInt(infoHabitacion?.precio, 10) || 0;
  };
  
  // Recalcular precios basados en los cambios
  const recalcularPrecios = () => {
    if (!reservaInfo || loadingHabitaciones) return;
    
    // Solo loguear si hay cambios pendientes
    if (tieneCambiosPendientes()) {
      console.log('Recalculando precios por cambios pendientes:', {
        cambiosFechas: cambiosPendientes.cambiosFechas,
        cambiosPersonas: cambiosPendientes.cambiosPersonas,
        habitacionesEliminar: cambiosPendientes.habitacionesEliminar
      });
    }
    
    const nuevosPrecios = {};
    let nuevoTotal = 0;
    
    // Fechas a usar para el cálculo
    const fechaEntrada = cambiosPendientes.cambiosFechas ? 
      cambiosPendientes.cambiosFechas.entrada : 
      reservaInfo.datosComunes.fechaEntrada;
      
    const fechaSalida = cambiosPendientes.cambiosFechas ? 
      cambiosPendientes.cambiosFechas.salida : 
      reservaInfo.datosComunes.fechaSalida;
    
    // Calcular para cada habitación que no esté marcada para eliminar
    reservaInfo.habitaciones.forEach(habitacion => {
      if (!cambiosPendientes.habitacionesEliminar.includes(habitacion.id)) {
        // Cantidad de personas actual o la nueva si ha cambiado
        const personas = cambiosPendientes.cambiosPersonas[habitacion.id] || habitacion.personasEnHabitacion;
        
        // Calcular precio base según ocupación (usando datos actuales de habitaciones)
        const precioBase = calcularPrecioPorPersonasDesdeHabitaciones(habitacion.habitacionId, personas);
        
        // Calcular precio total según días
        const precioTotal = calcularPrecioTotal(precioBase, fechaEntrada, fechaSalida);
        
        // Obtener precio base original para la diferencia
        const precioBaseOriginal = calcularPrecioPorPersonas(habitacion.habitacionId, habitacion.personasEnHabitacion);
        const precioTotalOriginal = calcularPrecioTotal(precioBaseOriginal, reservaInfo.datosComunes.fechaEntrada, reservaInfo.datosComunes.fechaSalida);
        
        nuevosPrecios[habitacion.id] = {
          precioBase,
          precioTotal,
          diferencia: precioTotal - precioTotalOriginal
        };
        
        nuevoTotal += precioTotal;
      }
    });
    
    setPreciosRecalculados(nuevosPrecios);
    setTotalRecalculado(nuevoTotal);
  };
  
  // Efecto para calcular el precio total inicial una vez cargados los datos
  useEffect(() => {
    // Llama a recalcularPrecios cuando la reserva y los datos de habitaciones están listos
    if (reservaInfo && !loadingHabitaciones && habitacionesData.length > 0) { 
      console.log("Calculando precios iniciales y guardando estado inicial...");
      
      // Calcular total inicial basado en Firestore
      let totalInicial = 0;
      const preciosIniciales = {}; // También calcular precios individuales iniciales
      reservaInfo.habitaciones.forEach(habitacion => {
        const precioBase = calcularPrecioPorPersonas(habitacion.habitacionId, habitacion.personasEnHabitacion); 
        const precioTotalHabitacion = calcularPrecioTotal(precioBase, reservaInfo.datosComunes.fechaEntrada, reservaInfo.datosComunes.fechaSalida);
        totalInicial += precioTotalHabitacion;
        preciosIniciales[habitacion.id] = { // Guardar precios base y total iniciales por habitación
          precioBase,
          precioTotal: precioTotalHabitacion,
          diferencia: 0 // Diferencia inicial es 0
        };
      });
      
      setPrecioInicialFirestore(totalInicial); // Guarda el total inicial basado en Firestore
      
      // Si NO hay cambios pendientes aún, actualizar los estados de display
      if (!tieneCambiosPendientes()) {
        setTotalRecalculado(totalInicial);
        setPreciosRecalculados(preciosIniciales);
      }
      console.log("Precio Inicial Firestore guardado:", totalInicial);
    }
  }, [reservaInfo, loadingHabitaciones, habitacionesData]); // Ya no necesita recalcularPrecios aquí
  
  // Efecto para recalcular precios cuando hay cambios pendientes
  useEffect(() => {
    // Recalcula solo si hay cambios PENDIENTES y los datos están cargados
    if (reservaInfo && !loadingHabitaciones && tieneCambiosPendientes()) { 
      console.log("Recalculando precios por cambios pendientes...");
      recalcularPrecios(); // Esta función actualiza preciosRecalculados y totalRecalculado para reflejar los cambios
    }
    // Usar useCallback para recalcularPrecios si da warning de dependencia
  }, [cambiosPendientes, reservaInfo, loadingHabitaciones, recalcularPrecios]); 
  
  // Función para buscar reserva directamente (sin evento del formulario)
  const buscarReservaPorCodigoDirecto = async (codigo) => {
    if (!codigo.trim()) {
      return;
    }
    
    setLoading(true);
    setErrorMensaje('');
    
    try {
      const resultado = await buscarReservaPorCodigo(codigo.trim());
      setReservaInfo(resultado);
      
      // Resetear los estados de cambios al cargar una nueva reserva
      setCambiosPendientes({
        habitacionesEliminar: [],
        cambiosFechas: null,
        cambiosPersonas: {}
      });
      setPreciosRecalculados({});
      setTotalRecalculado(0);
      setMostrarResumen(false);
      
    } catch (error) {
      console.error('Error al buscar la reserva:', error);
      setErrorMensaje(error.message || 'No se encontró ninguna reserva con ese código');
      setReservaInfo(null);
    } finally {
      setLoading(false);
    }
  };
  
  const handleBuscarReserva = async (e) => {
    e.preventDefault();
    
    if (!codigoReserva.trim()) {
      setErrorMensaje('Por favor, introduce un código de reserva');
      return;
    }
    
    // Redirigir a la URL con el código de reserva
    navigate(`/gestionar-reservas/${codigoReserva.trim()}`);
  };

  const handleSeleccionarEliminarHabitacion = (habitacionId) => {
    // Marcar/desmarcar la habitación para eliminar
    setCambiosPendientes(prevState => {
      const nuevasHabitaciones = [...prevState.habitacionesEliminar];
      
      if (nuevasHabitaciones.includes(habitacionId)) {
        // Si ya está seleccionada, desmarcarla
        return {
          ...prevState,
          habitacionesEliminar: nuevasHabitaciones.filter(id => id !== habitacionId)
        };
      } else {
        // Si no está seleccionada, marcarla
        return {
          ...prevState,
          habitacionesEliminar: [...nuevasHabitaciones, habitacionId]
        };
      }
    });
  };

  const handleEliminarHabitacion = async (habitacionId) => {
    setHabitacionSeleccionada(habitacionId);
    setModalTipo('confirmarEliminar');
    setModalTitulo('Confirmar eliminación');
    setModalMensaje('¿Estás seguro de que quieres eliminar esta habitación de la reserva?');
    setModalAbierto(true);
  };

  const confirmarEliminarHabitacion = async () => {
    if (!habitacionSeleccionada) return;
    
    setModalAbierto(false);
    setLoading(true);
    
    try {
      const resultado = await eliminarHabitacionDeReserva(habitacionSeleccionada);
      
      // Actualizar la información de la reserva
      if (reservaInfo.habitaciones.length <= 1) {
        // Si era la única habitación, eliminar toda la reserva
        setReservaInfo(null);
        setModalTipo('mensaje');
        setModalTitulo('Reserva eliminada');
        setModalMensaje('La reserva ha sido eliminada completamente ya que no quedaban habitaciones');
      } else {
        // Actualizar la lista de habitaciones
        const habitacionesActualizadas = reservaInfo.habitaciones.filter(
          h => h.id !== habitacionSeleccionada
        );
        
        // Recalcular el precio total
        const nuevoTotal = habitacionesActualizadas.reduce(
          (sum, h) => sum + (h.precioTotal || 0), 0
        );
        
        setReservaInfo({
          ...reservaInfo,
          habitaciones: habitacionesActualizadas,
          cantidadHabitaciones: habitacionesActualizadas.length,
          precioTotal: nuevoTotal
        });
        
        setModalTipo('mensaje');
        setModalTitulo('Habitación eliminada');
        setModalMensaje('La habitación ha sido eliminada de la reserva');
      }
      
      setModalAbierto(true);
    } catch (error) {
      console.error('Error al eliminar la habitación:', error);
      setModalTipo('error');
      setModalTitulo('Error');
      setModalMensaje(error.message || 'Ocurrió un error al eliminar la habitación');
      setModalAbierto(true);
    } finally {
      setLoading(false);
      setHabitacionSeleccionada(null);
    }
  };

  const handleAnularReserva = () => {
    setModalTipo('confirmarAnular');
    setModalTitulo('Confirmar anulación');
    setModalMensaje('¿Estás seguro de que quieres anular completamente esta reserva? Esta acción no se puede deshacer.');
    setModalAbierto(true);
  };

  const confirmarAnularReserva = async () => {
    setModalAbierto(false);
    setLoading(true);
    
    try {
      const resultado = await anularReservaPorCodigo(codigoReserva);
      
      setReservaInfo(null);
      setCodigoReserva('');
      
      setModalTipo('mensaje');
      setModalTitulo('Reserva anulada');
      setModalMensaje(`La reserva ha sido anulada correctamente. Se eliminaron ${resultado.cantidad} habitaciones.`);
      setModalAbierto(true);
    } catch (error) {
      console.error('Error al anular la reserva:', error);
      setModalTipo('error');
      setModalTitulo('Error');
      setModalMensaje(error.message || 'Ocurrió un error al anular la reserva');
      setModalAbierto(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCambiarPersonas = (habitacionId, capacidadMaxima) => {
    // Encontrar la habitación para obtener el valor actual
    const habitacion = reservaInfo.habitaciones.find(h => h.id === habitacionId);
    if (habitacion) {
      setHabitacionSeleccionada(habitacionId);
      // Inicializar con el valor actual o el valor pendiente de cambio
      const valorActual = cambiosPendientes.cambiosPersonas[habitacionId] || habitacion.personasEnHabitacion;
      setNuevaCantidadPersonas(valorActual);
      setModalTipo('cambiarPersonas');
      setModalTitulo('Cambiar número de personas');
      setModalMensaje(`Selecciona el número de personas para esta habitación (máximo ${capacidadMaxima})`);
      setModalAbierto(true);
    }
  };

  const confirmarCambiarPersonas = () => {
    if (!habitacionSeleccionada) return;
    
    // Guardar el cambio pendiente
    setCambiosPendientes(prevState => ({
      ...prevState,
      cambiosPersonas: {
        ...prevState.cambiosPersonas,
        [habitacionSeleccionada]: nuevaCantidadPersonas
      }
    }));
    
    setModalAbierto(false);
    setHabitacionSeleccionada(null);
  };

  const handleCambiarFechas = () => {
    if (!reservaInfo) return;
    
    // Inicializar con los valores actuales o los valores pendientes de cambio
    const fechaEntrada = cambiosPendientes.cambiosFechas ? 
      cambiosPendientes.cambiosFechas.entrada : 
      reservaInfo.datosComunes.fechaEntrada;
      
    const fechaSalida = cambiosPendientes.cambiosFechas ? 
      cambiosPendientes.cambiosFechas.salida : 
      reservaInfo.datosComunes.fechaSalida;
    
    setNuevasFechas({
      entrada: dayjs(fechaEntrada),
      salida: dayjs(fechaSalida)
    });
    
    setModalTipo('cambiarFechas');
    setModalTitulo('Cambiar fechas de la reserva');
    setModalMensaje('Selecciona las nuevas fechas de entrada y salida');
    setModalAbierto(true);
  };

  const confirmarCambiarFechas = () => {
    if (!nuevasFechas.entrada || !nuevasFechas.salida) {
      alert('Por favor, selecciona fechas válidas');
      return;
    }
    
    const formatoFecha = fecha => fecha.format('YYYY-MM-DD');
    
    // Guardar el cambio pendiente
    setCambiosPendientes(prevState => ({
      ...prevState,
      cambiosFechas: {
        entrada: formatoFecha(nuevasFechas.entrada),
        salida: formatoFecha(nuevasFechas.salida)
      }
    }));
    
    setModalAbierto(false);
  };

  const handleGuardarCambios = () => {
    if (!tieneCambiosPendientes()) {
      alert('No hay cambios para guardar');
      return;
    }
    
    setMostrarResumen(true);
  };
  
  const confirmarAplicarCambios = async () => {
    setMostrarResumen(false);
    setLoading(true);
    
    try {
      // 1. Procesar cambios de fechas si existen
      if (cambiosPendientes.cambiosFechas) {
        await actualizarFechasReserva(
          codigoReserva,
          cambiosPendientes.cambiosFechas.entrada,
          cambiosPendientes.cambiosFechas.salida
        );
      }
      
      // 2. Procesar cambios de personas en habitaciones
      const promesasCambioPersonas = Object.entries(cambiosPendientes.cambiosPersonas).map(
        ([habitacionId, nuevaCantidad]) => 
          actualizarPersonasEnHabitacion(habitacionId, nuevaCantidad)
      );
      
      if (promesasCambioPersonas.length > 0) {
        await Promise.all(promesasCambioPersonas);
      }
      
      // 3. Procesar eliminaciones de habitaciones
      const promesasEliminacion = cambiosPendientes.habitacionesEliminar.map(
        habitacionId => eliminarHabitacionDeReserva(habitacionId)
      );
      
      if (promesasEliminacion.length > 0) {
        await Promise.all(promesasEliminacion);
      }
      
      // 4. Volver a buscar la reserva para actualizar los datos
      const reservaActualizada = await buscarReservaPorCodigo(codigoReserva);
      
      // 5. Verificar si quedaron habitaciones, si no, la reserva fue eliminada
      if (!reservaActualizada || reservaActualizada.cantidadHabitaciones === 0) {
        setReservaInfo(null);
        setCodigoReserva('');
        
        setModalTipo('mensaje');
        setModalTitulo('Reserva actualizada');
        setModalMensaje('La reserva ha sido completamente eliminada ya que no quedaron habitaciones.');
      } else {
        setReservaInfo(reservaActualizada);
        
        setModalTipo('mensaje');
        setModalTitulo('Reserva actualizada');
        setModalMensaje('Los cambios en la reserva han sido aplicados correctamente.');
      }
      
      // 6. Enviar correo de confirmación con los cambios
      try {
        // Preparar datos para el correo
        const reservaParaCorreo = {
          id: reservaActualizada.id,
          nombre: reservaActualizada.datosComunes.nombre,
          email: reservaActualizada.datosComunes.email,
          telefono: reservaActualizada.datosComunes.telefono,
          fechaEntrada: reservaActualizada.datosComunes.fechaEntrada,
          fechaSalida: reservaActualizada.datosComunes.fechaSalida,
          habitaciones: reservaActualizada.habitaciones.map(h => {
            const infoHabitacion = obtenerInfoHabitacion(h.habitacionId);
            
            // Obtener el precio por noche guardado en la reserva o calcularlo si no existe
            let precioNocheActual;
            
            // Primero intentar obtener el precio personalizado guardado
            if (h.precioPorNoche) {
              precioNocheActual = h.precioPorNoche;
              console.log(`Correo: Usando precio personalizado (precioPorNoche): ${precioNocheActual} para habitación ${h.id}`);
            } else if (h.pricePerNight) {
              precioNocheActual = h.pricePerNight;
              console.log(`Correo: Usando precio personalizado (pricePerNight): ${precioNocheActual} para habitación ${h.id}`);
            } else if (h.precioHabitacion) {
              precioNocheActual = h.precioHabitacion;
              console.log(`Correo: Usando precio de habitación (precioHabitacion): ${precioNocheActual} para habitación ${h.id}`);
            } else if (h.roomPrice) {
              precioNocheActual = h.roomPrice;
              console.log(`Correo: Usando precio de habitación (roomPrice): ${precioNocheActual} para habitación ${h.id}`);
            } else {
              // Si no hay precio guardado, calcularlo basado en ocupación
              precioNocheActual = calcularPrecioPorPersonas(h.habitacionId, h.personasEnHabitacion);
              console.log(`Correo: Calculando precio por ocupación: ${precioNocheActual} para habitación ${h.id}`);
            }
            
            return {
              id: h.id,
              nombre: infoHabitacion.nombre,
              tipo: infoHabitacion.tipo,
              capacidad: infoHabitacion.capacity, 
              precio: precioNocheActual, // <-- Usar el precio guardado o calculado
              preciosPorOcupacion: infoHabitacion.preciosPorOcupacion || {}
            };
          }),
          distribucionPersonas: reservaActualizada.habitaciones.reduce((acc, h) => {
            acc[h.id] = h.personasEnHabitacion;
            return acc;
          }, {}),
          duracionEstancia: calcularDiferenciaDias(
            reservaActualizada.datosComunes.fechaEntrada,
            reservaActualizada.datosComunes.fechaSalida
          ),
          // Usar el precio total final recalculado del estado
          precioTotal: totalRecalculado, 
          codigoReserva: reservaActualizada.datosComunes.codigoReserva
        };
        
        console.log('Datos preparados para el correo:', JSON.stringify(reservaParaCorreo, null, 2));
        
        // Enviar correo de confirmación como si fuera una reserva pública
        const resultadoCorreo = await enviarCorreoConfirmacion(reservaParaCorreo, true);
        
        if (resultadoCorreo) {
          console.log('Correo de confirmación enviado exitosamente');
          setModalMensaje(prev => prev.includes('aplicados correctamente') ? 'Los cambios en la reserva han sido aplicados correctamente. Se ha enviado un correo de confirmación con los detalles actualizados.' : prev);
        } else {
          console.warn('No se pudo enviar el correo de confirmación');
          setModalMensaje(prev => prev.includes('aplicados correctamente') ? 'Los cambios en la reserva han sido aplicados correctamente, pero no se pudo enviar el correo de confirmación.' : prev);
        }
      } catch (emailError) {
        console.error('Error al enviar correo de confirmación:', emailError);
        setModalMensaje(prev => prev.includes('aplicados correctamente') ? 'Los cambios en la reserva han sido aplicados correctamente, pero hubo un error al enviar el correo de confirmación.' : prev);
      }
      
      // Resetear los cambios pendientes
      setCambiosPendientes({
        habitacionesEliminar: [],
        cambiosFechas: null,
        cambiosPersonas: {}
      });
      
      setModalAbierto(true);
    } catch (error) {
      console.error('Error al aplicar los cambios:', error);
      setModalTipo('error');
      setModalTitulo('Error');
      setModalMensaje(error.message || 'Ocurrió un error al aplicar los cambios');
      setModalAbierto(true);
    } finally {
      setLoading(false);
    }
  };
  
  const cancelarCambios = () => {
    // Resetear todos los cambios pendientes
    setCambiosPendientes({
      habitacionesEliminar: [],
      cambiosFechas: null,
      cambiosPersonas: {}
    });
    setMostrarResumen(false);
  };
  
  const tieneCambiosPendientes = () => {
    return cambiosPendientes.habitacionesEliminar.length > 0 || 
           Object.keys(cambiosPendientes.cambiosPersonas).length > 0 || 
           cambiosPendientes.cambiosFechas !== null;
  };
  
  const cerrarModal = () => {
    setModalAbierto(false);
  };

  return (
    <div className="gestion-reservas-container">
      <div className="gestion-reservas-header">
        <h2>Gestión de Reservas</h2>
        <p className="instrucciones">
          Introduce el código de reserva para ver los detalles y gestionar tu reserva.
        </p>
        {/* Mostrar indicador de carga de habitaciones */}
        {loadingHabitaciones && <p>Cargando datos de habitaciones...</p>}
      </div>

      <form onSubmit={handleBuscarReserva} className="form-buscar-reserva">
        <div className="form-group">
          <label htmlFor="codigoReserva">Código de reserva:</label>
          <div className="input-group">
            <input
              type="text"
              id="codigoReserva"
              ref={inputCodigoRef}
              value={codigoReserva}
              onChange={(e) => setCodigoReserva(e.target.value)}
              placeholder="Introduce tu código de 6 dígitos"
              maxLength="6"
              disabled={loading || loadingHabitaciones}
            />
            <button type="submit" className="btn-buscar" disabled={loading || loadingHabitaciones}>
              {loading ? 'Buscando...' : 'Buscar'}
            </button>
          </div>
        </div>
        {errorMensaje && <p className="error-mensaje">{errorMensaje}</p>}
      </form>

      {reservaInfo && (
        <div className="info-reserva">
          <div className="datos-reserva">
            <h3>Información de la reserva</h3>
            <div className="grid-datos">
              <div className="dato">
                <span>Código:</span>
                <strong>{reservaInfo.datosComunes.codigoReserva}</strong>
              </div>
              <div className="dato">
                <span>Nombre:</span>
                <strong>{reservaInfo.datosComunes.nombre}</strong>
              </div>
              <div className="dato">
                <span>Email:</span>
                <strong>{reservaInfo.datosComunes.email}</strong>
              </div>
              <div className="dato">
                <span>Teléfono:</span>
                <strong>{reservaInfo.datosComunes.telefono}</strong>
              </div>
              <div className="dato">
                <span>Fecha de entrada:</span>
                <strong>
                  {cambiosPendientes.cambiosFechas ? (
                    <span className="valor-modificado">
                      {formatearFechaVisual(cambiosPendientes.cambiosFechas.entrada)}
                      <small className="modificado-tag">(modificado)</small>
                    </span>
                  ) : (
                    formatearFechaVisual(reservaInfo.datosComunes.fechaEntrada)
                  )}
                </strong>
              </div>
              <div className="dato">
                <span>Fecha de salida:</span>
                <strong>
                  {cambiosPendientes.cambiosFechas ? (
                    <span className="valor-modificado">
                      {formatearFechaVisual(cambiosPendientes.cambiosFechas.salida)}
                      <small className="modificado-tag">(modificado)</small>
                    </span>
                  ) : (
                    formatearFechaVisual(reservaInfo.datosComunes.fechaSalida)
                  )}
                </strong>
              </div>
              <div className="dato">
                <span>Fecha de creación:</span>
                <strong>{reservaInfo.datosComunes.fechaCreacion}</strong>
              </div>
              <div className="dato total">
                <span>Precio total:</span>
                <strong>
                  {/* Mostrar totalRecalculado si es > 0, si no, el original */}
                  {totalRecalculado > 0 ? (
                     // Si hay cambios pendientes, mostrar diferencia 
                    tieneCambiosPendientes() ? (
                    <span className="valor-modificado">
                      {formatearPrecio(totalRecalculado)}
                      <small className="modificado-tag">
                          ({totalRecalculado >= reservaInfo.precioTotal ? '+' : ''} 
                        {formatearPrecio(totalRecalculado - reservaInfo.precioTotal)})
                      </small>
                    </span>
                  ) : (
                       // Si no hay cambios, solo mostrar el total recalculado 
                      formatearPrecio(totalRecalculado)
                    )
                  ) : (
                     // Si totalRecalculado es 0 (inicial o error), mostrar el original 
                    formatearPrecio(reservaInfo.precioTotal)
                  )}
                </strong>
              </div>
            </div>
            
            <div className="botones-principales">
              <button 
                className="btn-cambiar" 
                onClick={handleCambiarFechas}
                disabled={loading || loadingHabitaciones}
              >
                Cambiar fechas
              </button>
              
              {tieneCambiosPendientes() && (
                <>
                  <button 
                    className="btn-aplicar" 
                    onClick={handleGuardarCambios}
                    disabled={loading || loadingHabitaciones || !tieneCambiosPendientes()}
                  >
                    Finalizar modificación
                  </button>
                  <button 
                    className="btn-cancelar" 
                    onClick={cancelarCambios}
                    disabled={loading || loadingHabitaciones}
                  >
                    Cancelar cambios
                  </button>
                </>
              )}
              
              <button 
                className="btn-anular" 
                onClick={handleAnularReserva}
                disabled={loading || loadingHabitaciones}
              >
                Anular reserva
              </button>
            </div>
          </div>

          <div className="habitaciones-reservadas">
            <h3>Habitaciones reservadas</h3>
            {reservaInfo.habitaciones.length === 0 ? (
              <p className="sin-habitaciones">No hay habitaciones en esta reserva</p>
            ) : (
              <div className="lista-habitaciones">
                {reservaInfo.habitaciones.map((habitacion) => {
                  const infoHabitacion = obtenerInfoHabitacion(habitacion.habitacionId);
                  const estaSeleccionadaParaEliminar = cambiosPendientes.habitacionesEliminar.includes(habitacion.id);
                  const nuevaCantidadPersonas = cambiosPendientes.cambiosPersonas[habitacion.id];
                  const hayPrecioRecalculado = preciosRecalculados[habitacion.id];
                  
                  return (
                    <div 
                      key={habitacion.id} 
                      className={`tarjeta-habitacion ${estaSeleccionadaParaEliminar ? 'seleccionada-eliminar' : ''}`}
                    >
                      <div className="habitacion-header">
                        <h4>{infoHabitacion.nombre} {loadingHabitaciones ? '(Cargando...)' : ''}</h4>
                        <div className="acciones-habitacion">
                          <button 
                            className="btn-eliminar" 
                            onClick={() => handleSeleccionarEliminarHabitacion(habitacion.id)}
                            title={estaSeleccionadaParaEliminar ? "Cancelar eliminación" : "Marcar para eliminar"}
                            disabled={loading || loadingHabitaciones}
                          >
                            {estaSeleccionadaParaEliminar ? 'Cancelar eliminación' : 'Eliminar'}
                          </button>
                        </div>
                      </div>
                      
                      <div className="habitacion-detalles">
                        <div className="detalle">
                          <span>Tipo:</span>
                          <strong>{infoHabitacion.tipo || 'No disponible'}</strong>
                        </div>
                        <div className="detalle">
                          <span>Personas:</span>
                          <strong>
                            {nuevaCantidadPersonas ? (
                              <span className="valor-modificado">
                                {nuevaCantidadPersonas}
                                <small className="modificado-tag">(modificado)</small>
                              </span>
                            ) : (
                              habitacion.personasEnHabitacion
                            )}
                            <button 
                              className="btn-editar" 
                              onClick={() => handleCambiarPersonas(habitacion.id, infoHabitacion.capacity)}
                              title="Cambiar personas"
                              disabled={loading || loadingHabitaciones || estaSeleccionadaParaEliminar || !infoHabitacion?.capacity || infoHabitacion.capacity <= 0}
                            >
                              ✏️
                            </button>
                          </strong>
                        </div>
                        <div className="detalle">
                          <span>Precio por noche:</span>
                          <strong>
                            {/* Mostrar siempre precio base recalculado si existe */}
                            {preciosRecalculados[habitacion.id]?.precioBase !== undefined ? (
                              // Si hay cambios pendientes Y el precio base cambió, mostrar diferencia
                              tieneCambiosPendientes() && preciosRecalculados[habitacion.id]?.precioBase !== calcularPrecioPorPersonas(habitacion.habitacionId, habitacion.personasEnHabitacion) ? (
                              <span className="valor-modificado">
                                  {formatearPrecio(preciosRecalculados[habitacion.id].precioBase)}
                                <small className="modificado-tag">
                                    ({preciosRecalculados[habitacion.id].precioBase >= calcularPrecioPorPersonas(habitacion.habitacionId, habitacion.personasEnHabitacion) ? '+' : ''}
                                    {formatearPrecio(preciosRecalculados[habitacion.id].precioBase - calcularPrecioPorPersonas(habitacion.habitacionId, habitacion.personasEnHabitacion))})
                                </small>
                              </span>
                            ) : (
                                // Si no hay cambios pendientes o el precio no cambió, mostrar solo el valor
                                formatearPrecio(preciosRecalculados[habitacion.id].precioBase)
                              )
                            ) : (
                              // Fallback al precio base calculado desde Firestore si no hay recalculado
                              formatearPrecio(calcularPrecioPorPersonas(habitacion.habitacionId, habitacion.personasEnHabitacion))
                            )}
                          </strong>
                        </div>
                        <div className="detalle precio-total">
                          <span>Precio total:</span>
                          <strong>
                            {/* Mostrar siempre precio total recalculado si existe */}
                            {preciosRecalculados[habitacion.id]?.precioTotal !== undefined ? (
                              // Si hay cambios pendientes Y la diferencia existe (calculada en recalcularPrecios), mostrarla
                              tieneCambiosPendientes() && preciosRecalculados[habitacion.id]?.diferencia !== 0 ? (
                              <span className="valor-modificado">
                                  {formatearPrecio(preciosRecalculados[habitacion.id].precioTotal)}
                                <small className="modificado-tag">
                                    ({preciosRecalculados[habitacion.id].diferencia >= 0 ? '+' : ''}
                                    {formatearPrecio(preciosRecalculados[habitacion.id].diferencia)})
                                </small>
                              </span>
                            ) : (
                                // Si no hay cambios pendientes o no hay diferencia, mostrar solo el total
                                formatearPrecio(preciosRecalculados[habitacion.id].precioTotal)
                              )
                            ) : (
                              // Fallback al precio total calculado desde Firestore si no hay recalculado
                              formatearPrecio(calcularPrecioTotal(
                                calcularPrecioPorPersonas(habitacion.habitacionId, habitacion.personasEnHabitacion),
                                reservaInfo.datosComunes.fechaEntrada, 
                                reservaInfo.datosComunes.fechaSalida
                              ))
                            )}
                          </strong>
                        </div>
                      </div>
                      
                      {estaSeleccionadaParaEliminar && (
                        <div className="eliminacion-aviso">
                          Esta habitación será eliminada al finalizar la modificación
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal para diferentes acciones */}
      {modalAbierto && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{modalTitulo}</h3>
            
            {modalTipo === 'mensaje' && (
              <>
                <p>{modalMensaje}</p>
                <div className="modal-botones">
                  <button className="btn-aceptar" onClick={cerrarModal}>Aceptar</button>
                </div>
              </>
            )}
            
            {modalTipo === 'error' && (
              <>
                <p className="error-mensaje">{modalMensaje}</p>
                <div className="modal-botones">
                  <button className="btn-aceptar" onClick={cerrarModal}>Aceptar</button>
                </div>
              </>
            )}
            
            {modalTipo === 'confirmarEliminar' && (
              <>
                <p>{modalMensaje}</p>
                <div className="modal-botones">
                  <button className="btn-aceptar" onClick={confirmarEliminarHabitacion}>Confirmar</button>
                  <button className="btn-cancelar" onClick={cerrarModal}>Cancelar</button>
                </div>
              </>
            )}
            
            {modalTipo === 'confirmarAnular' && (
              <>
                <p>{modalMensaje}</p>
                <div className="modal-botones">
                  <button className="btn-anular" onClick={confirmarAnularReserva}>Confirmar anulación</button>
                  <button className="btn-cancelar" onClick={cerrarModal}>Cancelar</button>
                </div>
              </>
            )}
            
            {modalTipo === 'cambiarPersonas' && (
              <>
                <p>{modalMensaje}</p>
                <div className="selector-personas">
                  {Array.from({ length: obtenerInfoHabitacion(reservaInfo?.habitaciones.find(h => h.id === habitacionSeleccionada)?.habitacionId)?.capacity || 0 }, (_, i) => i + 1).map(num => (
                    <button
                      key={num}
                      className={`opcion-persona ${nuevaCantidadPersonas === num ? 'seleccionada' : ''}`}
                      onClick={() => setNuevaCantidadPersonas(num)}
                    >
                      {num}
                    </button>
                  ))}
                </div>
                <div className="modal-botones">
                  <button className="btn-aceptar" onClick={confirmarCambiarPersonas}>Aceptar</button>
                  <button className="btn-cancelar" onClick={cerrarModal}>Cancelar</button>
                </div>
              </>
            )}
            
            {modalTipo === 'cambiarFechas' && (
              <>
                <p>{modalMensaje}</p>
                <div className="selector-fechas">
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={esLocale}>
                    <div className="fecha-input">
                      <label>Fecha de entrada:</label>
                      <DatePicker
                        value={nuevasFechas.entrada}
                        onChange={(newValue) => setNuevasFechas({...nuevasFechas, entrada: newValue})}
                        format="DD/MM/YYYY"
                        disablePast
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                    </div>
                    <div className="fecha-input">
                      <label>Fecha de salida:</label>
                      <DatePicker
                        value={nuevasFechas.salida}
                        onChange={(newValue) => setNuevasFechas({...nuevasFechas, salida: newValue})}
                        format="DD/MM/YYYY"
                        disablePast
                        minDate={nuevasFechas.entrada ? nuevasFechas.entrada.add(1, 'day') : undefined}
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                    </div>
                  </LocalizationProvider>
                </div>
                <div className="modal-botones">
                  <button className="btn-aceptar" onClick={confirmarCambiarFechas}>Aceptar</button>
                  <button className="btn-cancelar" onClick={cerrarModal}>Cancelar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Modal de resumen de cambios */}
      {mostrarResumen && (
        <div className="modal-overlay">
          <div className="modal-content modal-resumen">
            <h3>Resumen de cambios</h3>
            
            <div className="resumen-cambios">
              {cambiosPendientes.cambiosFechas && (
                <div className="cambio-item">
                  <h4>Cambio de fechas</h4>
                  <p>De: {formatearFechaVisual(reservaInfo.datosComunes.fechaEntrada)} al {formatearFechaVisual(reservaInfo.datosComunes.fechaSalida)}</p>
                  <p>A: {formatearFechaVisual(cambiosPendientes.cambiosFechas.entrada)} al {formatearFechaVisual(cambiosPendientes.cambiosFechas.salida)}</p>
                </div>
              )}
              
              {Object.keys(cambiosPendientes.cambiosPersonas).length > 0 && (
                <div className="cambio-item">
                  <h4>Cambios en ocupación</h4>
                  <ul>
                    {Object.entries(cambiosPendientes.cambiosPersonas).map(([habitacionId, nuevaCantidad]) => {
                      const habitacion = reservaInfo.habitaciones.find(h => h.id === habitacionId);
                      if (!habitacion) return null;
                      
                      const infoHabitacion = obtenerInfoHabitacion(habitacion.habitacionId);
                      return (
                        <li key={habitacionId}>
                          {infoHabitacion.nombre}: de {habitacion.personasEnHabitacion} a {nuevaCantidad} personas
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              
              {cambiosPendientes.habitacionesEliminar.length > 0 && (
                <div className="cambio-item">
                  <h4>Habitaciones a eliminar</h4>
                  <ul>
                    {cambiosPendientes.habitacionesEliminar.map(habitacionId => {
                      const habitacion = reservaInfo.habitaciones.find(h => h.id === habitacionId);
                      if (!habitacion) return null;
                      
                      const infoHabitacion = obtenerInfoHabitacion(habitacion.habitacionId);
                      return (
                        <li key={habitacionId}>
                          {infoHabitacion.nombre}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              
              <div className="cambio-item cambio-precio">
                <h4>Cambio en precio total</h4>
                <p>Precio actual: {formatearPrecio(precioInicialFirestore)}</p>
                <p>Nuevo precio: {formatearPrecio(totalRecalculado)}</p>
                <p className="diferencia-precio">
                  Diferencia: 
                  <span className={totalRecalculado >= precioInicialFirestore ? 'incremento' : 'decremento'}>
                    {totalRecalculado >= precioInicialFirestore ? '+' : ''}
                    {formatearPrecio(totalRecalculado - precioInicialFirestore)}
                  </span>
                </p>
              </div>
            </div>
            
            <div className="modal-botones">
              <button className="btn-aplicar" onClick={confirmarAplicarCambios} disabled={loading}>Aplicar cambios</button>
              <button className="btn-cancelar" onClick={() => setMostrarResumen(false)} disabled={loading}>Volver</button>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Button */}
      <a 
        href="https://api.whatsapp.com/send/?phone=56950620081&text&type=phone_number&app_absent=0" 
        className="whatsapp-float"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Contactar por WhatsApp"
      >
        <svg viewBox="0 0 24 24" width="24" height="24" className="whatsapp-icon">
          <path fill="currentColor" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
}

export default GestionReservas; 